import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'elvis-theme';

export type Tema = 'ljust' | 'morkt';

/**
 * Ljust eller mörkt tema, sparat per webbläsare precis som bakgrundsbilden och
 * den hopfällda sidomenyn.
 *
 * Attributet sätts på <html>, inte på <body>. Tokens.css definierar mörkt läge
 * som `:root[data-theme='dark']`, och :root ÄR html-elementet — sätts attributet
 * på body träffar väljaren aldrig, och knappen hade bytt ingenting.
 */

/**
 * Läser ett SPARAT val. Returnerar null när användaren inte valt något.
 *
 * Åtkomsten är inslagen i try/catch, inte bara skrivningen: `localStorage`
 * KASTAR (SecurityError) redan vid uppslaget i Chrome när webbplatsdata är
 * blockerat — även `typeof localStorage`. Och den här funktionen körs i
 * `useState`-initieraren under render, så ett kast hade fällt hela appen ner i
 * ErrorBoundary i stället för att bara ge fel tema.
 */
function lasSparatVal(): Tema | null {
  try {
    const sparat = localStorage.getItem(STORAGE_KEY);
    return sparat === 'ljust' || sparat === 'morkt' ? sparat : null;
  } catch {
    return null;
  }
}

function systemetsVal(): Tema {
  try {
    return matchMedia('(prefers-color-scheme: dark)').matches ? 'morkt' : 'ljust';
  } catch {
    return 'ljust';
  }
}

export function useTheme() {
  /**
   * `null` betyder "inget eget val" — och skiljs MED FLIT från ett val som
   * råkar sammanfalla med systemets.
   *
   * Första versionen härledde temat ur systemet och sparade resultatet direkt
   * vid montering. Det gjorde att den som aldrig rört knappen ändå fick ett
   * uttryckligt val i lagringen, och systemets växling till ljust vid gryningen
   * slutade få effekt för alltid — raka motsatsen till vad kommentaren påstod.
   */
  const [egetVal, setEgetVal] = useState<Tema | null>(lasSparatVal);
  const [system, setSystem] = useState<Tema>(systemetsVal);

  // Följ systemet så länge användaren inte valt själv.
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const vid = () => setSystem(mq.matches ? 'morkt' : 'ljust');
    mq.addEventListener('change', vid);
    return () => mq.removeEventListener('change', vid);
  }, []);

  const tema: Tema = egetVal ?? system;

  // Bara attributet sätts här. Lagringen skrivs i toggle, alltså när
  // användaren faktiskt väljer.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (tema === 'morkt') {
      document.documentElement.dataset.theme = 'dark';
    } else {
      delete document.documentElement.dataset.theme;
    }
  }, [tema]);

  /*
   * Lagringen skrivs i en EFFEKT, inte inuti updateraren.
   *
   * React kan köra en state-updater flera gånger och kassera resultatet. En
   * skrivning därinne hade alltså kunnat spara ett värde som aldrig blev
   * committat — updateraren måste vara ren. Effekten kör bara på det värde som
   * faktiskt gäller.
   *
   * `null` skrivs aldrig: det betyder "inget eget val", och att spara det hade
   * varit att göra frånvaron av ett val till ett val.
   */
  useEffect(() => {
    if (egetVal === null) return;
    try {
      localStorage.setItem(STORAGE_KEY, egetVal);
    } catch {
      /* privat läge kastar — temat gäller ändå för den här fliken */
    }
  }, [egetVal]);

  const toggle = useCallback(() => {
    setEgetVal((nuvarande) => ((nuvarande ?? systemetsVal()) === 'morkt' ? 'ljust' : 'morkt'));
  }, []);

  return { tema, morkt: tema === 'morkt', toggle };
}
